import { createWebHistory, createRouter, RouterOptions } from 'vue-router';
import routes, { Meta } from './routes';
import appConfig from '../../app.config.json';
import { useUserStore } from '@/state/user';
import { useAccessTokenStore } from '@/state/accessToken';
import { useClosurePeriodStore } from '@/state/closurePeriod';

const router = createRouter({
  history: createWebHistory(),
  routes: routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
} as RouterOptions);

// API based Authentication
// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
  if (!authRequired) return next();

  const proxyLogin = routeTo.matched.some((route) => route.meta.proxyLogin);
  const accessToken = routeTo.query.token?.toString() ?? '';
  if (proxyLogin && accessToken) {
    useAccessTokenStore().setAccessToken(accessToken);
  }

  const token = useAccessTokenStore().getAccessToken;

  if (!token) {
    useUserStore().deleteUserName();
    useClosurePeriodStore().deleteClosurePeriod();
    return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  }

  next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        const meta = route.meta as Meta;
        if (meta && meta.beforeResolve) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta.beforeResolve(routeTo, routeFrom, (...args: any) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...(args as []));
              reject(new Error('Redirected'));
            } else {
              resolve('');
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve('');
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + ' | ' + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
